"use client";

import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

import Icon from "@/components/atoms/Icon";
import {
  ParagraphAccordionFieldsFragment,
  ParagraphAccordionItemFieldsFragment,
} from "@/graphql/sdk/__generated__";
import { cn } from "@/helpers/className";

interface AccordionParagraphProps {
  className?: string;
  data: ParagraphAccordionFieldsFragment;
  narrow?: boolean;
}

export const AccordionParagraph = ({
  data,
  className,
}: AccordionParagraphProps) => {
  const [openItemId, setOpenItemId] = useState<string | null>(null);

  return (
    <div className={cn(className, "paragraph--accordion container")}>
      <div className="grid grid-cols-12">
        <div className="col-span-12 xl:col-span-8 xl:col-start-3">
          {data.title ? (
            <h2 className="!mb-4 text-center xl:!mb-6">{data.title}</h2>
          ) : null}

          {data.description && (
            <div
              className="prose !mb-6 text-center font-bold leading-6 text-black-400 xl:!mb-12"
              dangerouslySetInnerHTML={{
                __html: data.description?.processed ?? "",
              }}
            />
          )}

          <div className="flex flex-col gap-1.5">
            {data.accordionItems.map((accordionItem) => (
              <AccordionParagraphItem
                key={accordionItem.id}
                data={accordionItem}
                isOpen={openItemId === accordionItem.id}
                onToggle={() => {
                  setOpenItemId((currentOpenItemId) =>
                    currentOpenItemId === accordionItem.id
                      ? null
                      : accordionItem.id,
                  );
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

interface AccordionParagraphItemProps {
  className?: string;
  data: ParagraphAccordionItemFieldsFragment;
  isOpen: boolean;
  onToggle: () => void;
}

const AccordionParagraphItem = ({
  data,
  isOpen,
  onToggle,
}: AccordionParagraphItemProps) => {
  const itemId = data.id;
  const buttonId = `${itemId}-button`;
  const panelId = `${itemId}-panel`;

  return (
    <div
      className={cn(
        "flex flex-col items-start rounded-lg bg-white px-6 py-4 transition-[border-color,filter] duration-200",
        isOpen
          ? "gap-4 border border-yellow-600 drop-shadow-[0_4px_10px_rgba(249,152,0,0.2)] hover:drop-shadow-[0_4px_10px_rgba(249,152,0,0.3)]"
          : "border border-transparent hover:border-yellow-600",
      )}
    >
      <button
        id={buttonId}
        type="button"
        aria-controls={panelId}
        aria-expanded={isOpen}
        className={cn(
          "group flex w-full items-center justify-between gap-3 text-left",
          !isOpen && "-mx-6 -my-4 w-[calc(100%+48px)] px-6 py-4",
        )}
        onClick={onToggle}
      >
        <span
          className={cn(
            "flex-1 break-words text-lg font-bold tracking-normal transition-colors duration-200",
            isOpen ? "text-black group-hover:text-orange" : "text-black",
            !isOpen && "group-hover:text-orange",
          )}
        >
          {data.title}
        </span>
        <Icon
          name="angle-down"
          className={cn(
            "h-[18px] w-[18px] shrink-0 text-black-300 transition-transform duration-200",
            isOpen && "rotate-180",
          )}
        />
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            id={panelId}
            role="region"
            aria-labelledby={buttonId}
            className="w-full overflow-hidden"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.24, ease: [0.4, 0, 0.2, 1] }}
          >
            <div
              className="prose max-w-none pr-9 text-base text-black-300"
              dangerouslySetInnerHTML={{
                __html: data.text.processed ?? "",
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
